import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Card, Select, Row, Col, Spin, Space } from "antd";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import {
    RiseOutlined, FallOutlined, UserDeleteOutlined,
    HeartOutlined, BarChartOutlined,
} from "@ant-design/icons";
import { AuthContext } from "../../context/AuthContext";

const { Option } = Select;

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];
const now = new Date();
const CURRENT_YEAR = now.getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i);

const METRIC_DEFS = [
    {
        key: "retention_rate",
        label: "Retention Rate",
        desc: "Employees retained vs. total headcount",
        icon: <HeartOutlined />,
        color: "#10b981", bg: "#f0fdf4", border: "#bbf7d0",
        goodWhen: "high",
        suffix: "%",
    },
    {
        key: "absenteeism_rate",
        label: "Absenteeism Rate",
        desc: "Average daily absence vs. workforce",
        icon: <FallOutlined />,
        color: "#f59e0b", bg: "#fffbeb", border: "#fde68a",
        goodWhen: "low",
        suffix: "%",
    },
    {
        key: "attrition_rate",
        label: "Attrition Rate",
        desc: "Employee turnover rate this period",
        icon: <UserDeleteOutlined />,
        color: "#ef4444", bg: "#fff1f2", border: "#fecaca",
        goodWhen: "low",
        suffix: "%",
    },
];

export default function HrAnalytics() {
    const { authData, backUrl } = useContext(AuthContext);
    const [metrics, setMetrics] = useState({});
    const [chartData, setChartData] = useState([]);
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [year, setYear] = useState(CURRENT_YEAR);
    const [organisations, setOrganisations] = useState([]);
    const [organisation, setOrganisation] = useState("ALL");
    const [loading, setLoading] = useState(false);
    const [hasRealData, setHasRealData] = useState(false);

    useEffect(() => {
        axios.get(`${backUrl}/api/emp/organisations/`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        }).then(res => setOrganisations(res.data.results || res.data || []))
          .catch(() => {});
    }, [backUrl, authData?.access]);

    useEffect(() => {
        setLoading(true);
        const params = { month, year };
        if (organisation !== "ALL") params.organisation_id = organisation;

        axios.get(`${backUrl}/api/reports/hr-analytics/`, {
            params,
            headers: { Authorization: `Bearer ${authData?.access}` },
        }).then(res => {
            const apiData = res.data || {};
            const retention = apiData.retention_rate;
            const absenteeism = apiData.absenteeism_rate;
            const attrition = apiData.attrition_rate;

            const realData = retention != null || absenteeism != null || attrition != null;
            setHasRealData(realData);

            const m = {
                retention_rate: retention ?? (realData ? 0 : null),
                absenteeism_rate: absenteeism ?? (realData ? 0 : null),
                attrition_rate: attrition ?? (realData ? 0 : null),
            };
            setMetrics(m);

            setChartData([
                { metric: "Retention", value: m.retention_rate ?? 0 },
                { metric: "Absenteeism", value: m.absenteeism_rate ?? 0 },
                { metric: "Attrition", value: m.attrition_rate ?? 0 },
            ]);
        }).catch(() => {
            setHasRealData(false);
            setMetrics({});
            setChartData([]);
        }).finally(() => setLoading(false));
    }, [organisation, month, year, backUrl, authData?.access]);

    const getTrend = (def, val) => {
        if (val == null) return null;
        if (def.goodWhen === "high" && val >= 90) return { label: "Excellent", color: "#10b981" };
        if (def.goodWhen === "high" && val >= 75) return { label: "Good", color: "#3b82f6" };
        if (def.goodWhen === "high" && val >= 60) return { label: "Average", color: "#f59e0b" };
        if (def.goodWhen === "high") return { label: "Critical", color: "#ef4444" };
        if (def.goodWhen === "low" && val <= 2) return { label: "Excellent", color: "#10b981" };
        if (def.goodWhen === "low" && val <= 5) return { label: "Good", color: "#3b82f6" };
        if (def.goodWhen === "low" && val <= 10) return { label: "Average", color: "#f59e0b" };
        return { label: "Critical", color: "#ef4444" };
    };

    return (
        <Card
            bordered={false}
            style={{
                borderRadius: 16, background: "#ffffff",
                boxShadow: "0 4px 16px rgba(15,23,42,0.04)",
                border: "1px solid #e2e8f0",
            }}
            bodyStyle={{ padding: "20px 22px" }}
        >
            {/* Header */}
            <Row justify="space-between" align="middle" gutter={[0, 12]} style={{ marginBottom: 18 }}>
                <Col xs={24} md={12}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{
                            width: 40, height: 40, borderRadius: 11,
                            background: "linear-gradient(135deg, #7c3aed, #a855f7)",
                            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                        }}>
                            <BarChartOutlined style={{ color: "#fff", fontSize: 18 }} />
                        </div>
                        <div>
                            <div style={{ fontWeight: 800, fontSize: 15, color: "#0f172a" }}>
                                Workforce Health & Retention Analytics
                            </div>
                            <div style={{ fontSize: 12, color: "#64748b", marginTop: 1 }}>
                                Key HR KPIs — {MONTHS[month - 1]} {year}
                                {!hasRealData && !loading && (
                                    <span style={{
                                        marginLeft: 8, fontSize: 10, fontWeight: 700,
                                        background: "#fef3c7", color: "#b45309",
                                        padding: "1px 7px", borderRadius: 8, border: "1px solid #fde68a",
                                    }}>
                                        No Data Available
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </Col>
                <Col xs={24} md={12}>
                    <Space wrap size={6} style={{ justifyContent: "flex-end", width: "100%" }}>
                        <Select value={organisation} onChange={setOrganisation} style={{ width: 175 }} size="small">
                            <Option value="ALL">All Organisations ({organisations.length})</Option>
                            {organisations.map(o => <Option key={o.id} value={o.id}>{o.name}</Option>)}
                        </Select>
                        <Select value={month} onChange={setMonth} style={{ width: 108 }} size="small">
                            {MONTHS.map((m, i) => <Option key={i + 1} value={i + 1}>{m}</Option>)}
                        </Select>
                        <Select value={year} onChange={setYear} style={{ width: 80 }} size="small">
                            {YEARS.map(y => <Option key={y} value={y}>{y}</Option>)}
                        </Select>
                    </Space>
                </Col>
            </Row>

            {loading ? (
                <div style={{ textAlign: "center", padding: "60px 0" }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 12, color: "#64748b", fontWeight: 600 }}>Loading HR Analytics...</div>
                </div>
            ) : (
                <>
                    {/* KPI Metric Cards */}
                    <Row gutter={[14, 14]} style={{ marginBottom: 24 }}>
                        {METRIC_DEFS.map(def => {
                            const val = metrics[def.key];
                            const trend = hasRealData ? getTrend(def, val) : null;
                            return (
                                <Col xs={24} sm={8} key={def.key}>
                                    <div style={{
                                        background: hasRealData && val != null ? def.bg : "#f8fafc",
                                        border: `1px solid ${hasRealData && val != null ? def.border : "#e2e8f0"}`,
                                        borderRadius: 14, padding: "16px 18px",
                                        textAlign: "center",
                                        position: "relative", overflow: "hidden",
                                    }}>
                                        <div style={{
                                            position: "absolute", top: 0, left: 0, right: 0, height: 3,
                                            background: hasRealData && val != null
                                                ? `linear-gradient(90deg, ${def.color}, ${def.color}88)`
                                                : "#e2e8f0",
                                        }} />
                                        <div style={{
                                            width: 36, height: 36, borderRadius: 10, margin: "0 auto 10px",
                                            background: hasRealData && val != null ? `${def.color}18` : "#f1f5f9",
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            fontSize: 18, color: hasRealData && val != null ? def.color : "#94a3b8",
                                        }}>
                                            {def.icon}
                                        </div>
                                        <div style={{
                                            fontSize: 32, fontWeight: 900,
                                            color: hasRealData && val != null ? def.color : "#94a3b8",
                                            lineHeight: 1, marginBottom: 4,
                                        }}>
                                            {hasRealData && val != null ? `${val}${def.suffix}` : "—"}
                                        </div>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a", marginBottom: 4 }}>
                                            {def.label}
                                        </div>
                                        <div style={{ fontSize: 11, color: "#64748b", marginBottom: trend ? 8 : 0 }}>
                                            {def.desc}
                                        </div>
                                        {trend && (
                                            <span style={{
                                                fontSize: 10, fontWeight: 800, color: trend.color,
                                                background: `${trend.color}18`,
                                                border: `1px solid ${trend.color}30`,
                                                padding: "2px 9px", borderRadius: 8, display: "inline-block",
                                            }}>
                                                {trend.label}
                                            </span>
                                        )}
                                        {!hasRealData && (
                                            <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 4 }}>
                                                No data for period
                                            </div>
                                        )}
                                    </div>
                                </Col>
                            );
                        })}
                    </Row>

                    {/* Bar Chart */}
                    {hasRealData && chartData.length > 0 && (
                        <>
                            <div style={{ fontSize: 13, fontWeight: 700, color: "#334155", marginBottom: 10 }}>
                                Metric Overview — {MONTHS[month - 1]} {year}
                            </div>
                            <ResponsiveContainer width="100%" height={220}>
                                <BarChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                    <XAxis dataKey="metric" tick={{ fill: "#64748b", fontSize: 13, fontWeight: 600 }} />
                                    <YAxis domain={[0, 100]} tick={{ fill: "#64748b", fontSize: 12 }} tickFormatter={v => `${v}%`} />
                                    <Tooltip
                                        formatter={v => [`${v}%`]}
                                        contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }}
                                    />
                                    <Bar
                                        dataKey="value"
                                        name="Rate (%)"
                                        radius={[8, 8, 0, 0]}
                                        barSize={60}
                                        fill="#7c3aed"
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </>
                    )}
                </>
            )}
        </Card>
    );
}
